import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { getFirebase } from "../../core/firebaseApp";
import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS } from "../../core/collections";
import { getOrFetch, invalidate as _invalidate } from "../../cache/getOrFetch";

const TTL_APPUSER = Number(process.env.REACT_APP_TTL_APPUSER_MS) || 15 * 60 * 1000;

// keep your original
export function onAuthChangedSafe(cb) {
  const { auth, ready } = getFirebase();
  if (!ready) {
    const t = setTimeout(() => cb(null), 0);
    return () => clearTimeout(t);
  }
  return onAuthStateChanged(auth, cb);
}

// live subscribe to app_users/<uid>
export function subscribeAppUserProfile({ user, uid, onData, onError } = {}) {
  const db = getDbOrThrow();
  const id = String(uid || user?.uid || "").trim();
  if (!id) throw new Error("subscribeAppUserProfile: missing uid");
  const colName = (COLLECTIONS && COLLECTIONS.systemAppUsers) || "app_users";
  const ref = doc(db, colName, id);
  return onSnapshot(
    ref,
    (snap) => onData?.(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => onError?.(err)
  );
}

// one-shot read with cache
export async function fetchAppUserProfile({ user, uid, noCache, ttlMs } = {}) {
  const db = getDbOrThrow();
  const id = String(uid || user?.uid || "").trim();
  if (!id) throw new Error("fetchAppUserProfile: missing uid");
  const colName = (COLLECTIONS && COLLECTIONS.systemAppUsers) || "app_users";

  if (noCache) {
    const snap = await getDoc(doc(db, colName, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }

  const key = `appuser:${id}`;
  return getOrFetch({
    key,
    ttlMs: ttlMs ?? TTL_APPUSER,
    persist: true,
    persistKey: key,
    fetcher: async () => {
      const snap = await getDoc(doc(db, colName, id));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },
  });
}

export function invalidateAppUserProfileCache(uid) {
  const id = String(uid || "").trim();
  if (!id) return;
  _invalidate(`appuser:${id}`);
}
